import { useEffect, useState } from 'react';
import {
  buscarMeuWhatsapp,
  definirMeuWhatsapp,
  salvarTelefoneNoPerfil,
  formatarWhatsapp,
  digitosWhats,
} from '@/lib/whatsapp';
import { mostrarToast } from '@/lib/formularioGuiado';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';
import Campo from '@/components/ui/Campo';
import Botao from '@/components/ui/Botao';

// ---------------------------------------------------------------------------
// Linha "WhatsApp" no perfil, junto de "Avisos no celular": chave Ligado/
// Desligado e o número embaixo. O estado vem de `meu_whatsapp` (aceita null =
// desligado). Ligar/desligar chama `definir_meu_whatsapp`; ligar sem telefone
// abre o campo para digitar o número (e grava no perfil pelo caminho de
// "Meus dados").
// ---------------------------------------------------------------------------
export default function WhatsappPerfil() {
  const [carregando, setCarregando] = useState(true);
  const [ligado, setLigado] = useState(false);
  const [telefone, setTelefone] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [pedindoNumero, setPedindoNumero] = useState(false);
  const [numero, setNumero] = useState('');

  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        const d = await buscarMeuWhatsapp();
        if (!vivo) return;
        setTelefone(d.telefone);
        setLigado(d.aceita === true);
      } catch (e) {
        if (vivo) setErro(mensagemAmigavel(e));
      } finally {
        if (vivo) setCarregando(false);
      }
    })();
    return () => {
      vivo = false;
    };
  }, []);

  const temTelefone = digitosWhats(telefone).length >= 10;

  async function aplicar(valor: boolean, numeroInformado?: string | null) {
    setOcupado(true);
    setErro(null);
    try {
      await definirMeuWhatsapp(valor, numeroInformado ?? null);
      if (valor && numeroInformado) await salvarTelefoneNoPerfil(numeroInformado);
      if (numeroInformado) setTelefone(numeroInformado);
      setLigado(valor);
      setPedindoNumero(false);
      mostrarToast(
        valor ? 'WhatsApp ligado. Você vai receber por aqui.' : 'WhatsApp desligado.',
        4000,
        valor ? 'sucesso' : 'neutro',
      );
    } catch (e) {
      setErro(mensagemAmigavel(e, 'Não conseguimos mudar agora.'));
    } finally {
      setOcupado(false);
    }
  }

  function alternar() {
    if (ocupado) return;
    if (ligado) {
      void aplicar(false);
      return;
    }
    if (temTelefone) {
      void aplicar(true);
      return;
    }
    setNumero('');
    setErro(null);
    setPedindoNumero(true);
  }

  function confirmarNumero() {
    if (digitosWhats(numero).length < 10) {
      setErro('Digite seu WhatsApp com DDD.');
      return;
    }
    void aplicar(true, numero);
  }

  if (carregando) {
    return <div className="h-[88px] w-full animate-pulse rounded-lg bg-secondary-100" />;
  }

  return (
    <div className="rounded-lg border border-secondary-100 bg-white p-4">
      <div className="flex items-start gap-3">
        <span
          className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${
            ligado ? 'bg-success-100 text-success-700' : 'bg-background-100 text-foreground-500'
          }`}
        >
          <i className={ligado ? 'ri-whatsapp-fill' : 'ri-whatsapp-line'} aria-hidden />
        </span>

        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-foreground-950">WhatsApp</p>
          <p className={`mt-0.5 truncate text-sm ${ligado ? 'text-success-700' : 'text-foreground-600'}`}>
            {ligado ? 'Ligado' : 'Desligado'}
            {temTelefone ? ` · ${formatarWhatsapp(telefone)}` : ''}
          </p>
        </div>

        <button
          type="button"
          role="switch"
          aria-checked={ligado}
          aria-label="Receber pelo WhatsApp"
          onClick={alternar}
          disabled={ocupado}
          className={`aperta relative mt-0.5 h-7 w-12 shrink-0 cursor-pointer rounded-full transition-colors disabled:opacity-60 ${
            ligado ? 'bg-success-500' : 'bg-secondary-300'
          }`}
        >
          <span
            className={`absolute top-0.5 h-6 w-6 rounded-full bg-white transition-all ${
              ligado ? 'left-[22px]' : 'left-0.5'
            }`}
          />
        </button>
      </div>

      {!temTelefone && !pedindoNumero && (
        <p className="mt-2 text-[13px] text-foreground-500">
          Sem número no perfil. Ligue para informar o seu WhatsApp.
        </p>
      )}

      {pedindoNumero && (
        <div className="mt-3">
          <Campo
            rotulo="Seu WhatsApp com DDD"
            value={numero}
            onChange={(v) => {
              setNumero(v);
              setErro(null);
            }}
            mascara="telefone"
            placeholder="(00) 00000-0000"
            inputMode="tel"
            autoComplete="tel"
          />
          <div className="mt-3 flex flex-wrap gap-2">
            <Botao
              variante="primario"
              icone="ri-check-line"
              carregando={ocupado}
              onClick={confirmarNumero}
            >
              Salvar e ligar
            </Botao>
            <Botao
              variante="secundario"
              disabled={ocupado}
              onClick={() => {
                setPedindoNumero(false);
                setErro(null);
              }}
            >
              Cancelar
            </Botao>
          </div>
        </div>
      )}

      {erro && (
        <p className="mt-2 flex items-start gap-1.5 text-[13px] text-danger-600" role="alert">
          <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center">
            <i className="ri-error-warning-line" aria-hidden />
          </span>
          <span className="min-w-0">{erro}</span>
        </p>
      )}
    </div>
  );
}